import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { ImageLightbox } from '@/components/shared/image-lightbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MemberPhoto } from '@/components/ui/member-photo';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { resolveApiError } from '@/lib/api-errors';
import { MemberCreateDialog } from '@/pages/people/member-create-dialog';
import {
  formToPayload,
  type MemberFormState,
} from '@/pages/people/member-form-state';
import { formatDate } from '@/utils/format';

import { splitSacramentPersonName } from './sacrament-people-fields';
import {
  certificateFor,
  dateFor,
  marriageMinisterNames,
  personFor,
  type MarriageWitness,
  type RegisterKind,
  type RegisterRow,
} from './sacrament-register-types';

const DetailField = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <dt className="text-caption text-fg-secondary">{label}</dt>
    <dd className="text-body-sm text-fg">{children}</dd>
  </div>
);

const display = (value: string | null | undefined) => value?.trim() || '—';

const sameText = (left: string, right: string | null | undefined) =>
  left.trim() === (right ?? '').trim();

const memberFormFromRegister = (kind: RegisterKind, row: RegisterRow): Partial<MemberFormState> => {
  const parts =
    row.firstName || row.lastName
      ? {
          firstName: row.firstName ?? '',
          middleName: row.middleName ?? '',
          lastName: row.lastName ?? '',
        }
      : splitSacramentPersonName(row.personName ?? '');
  return {
    ...parts,
    motherName: row.motherName ?? '',
    fatherName: row.fatherName ?? '',
    photoKey: row.photoKey ?? null,
    photoPreviewUrl: row.photoUrl ?? null,
    notes: row.notes ?? '',
    memberType: 'member',
    ...(kind === 'baptisms'
      ? {
          baptismDate: row.baptismDate ?? null,
          baptismNumber: row.baptismNumber ?? '',
          baptismPlace: row.place ?? '',
          baptismMinister: row.ministerName ?? '',
          baptismType: row.baptismType ?? null,
        }
      : {}),
    ...(kind === 'confirmations'
      ? {
          confirmationDate: row.confirmationDate ?? null,
          confirmationNumber: row.confirmationNumber ?? '',
          confirmationPlace: row.place ?? '',
        }
      : {}),
  };
};

export const SacramentRegisterDetailDialog = ({
  kind,
  rowId,
  open,
  canEdit,
  canCreateMember,
  onOpenChange,
  onEdit,
}: {
  kind: RegisterKind;
  rowId: string | null;
  open: boolean;
  canEdit: boolean;
  canCreateMember: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (row: RegisterRow) => void;
}) => {
  const { t } = useTranslation('sacraments');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const [photoIndex, setPhotoIndex] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const detail = useQuery({
    queryKey: ['sacraments', kind, rowId],
    queryFn: () => api.get<RegisterRow>(`/sacraments/${kind}/${rowId}`),
    enabled: open && Boolean(rowId),
  });

  const row = detail.data;
  const showFamily = kind === 'baptisms' || kind === 'confirmations';
  const personName = row ? personFor(kind, row) : '';

  const createProfile = useMutation({
    mutationFn: async (form: MemberFormState) => {
      if (!row || !rowId) throw new Error('REGISTER_ROW_MISSING');
      const payload = formToPayload(form);
      if (kind === 'baptisms') {
        payload.baptismDate = null;
        payload.baptismNumber = null;
        payload.baptismPlace = null;
        payload.baptismMinister = null;
        payload.baptismType = null;
      }
      if (kind === 'confirmations') {
        payload.confirmationDate = null;
        payload.confirmationNumber = null;
        payload.confirmationPlace = null;
      }
      const member = await api.post<{ id: string }>('/people/members', payload);
      const shared = {
        memberId: member.id,
        firstName: form.firstName.trim(),
        middleName: form.middleName.trim() || null,
        lastName: form.lastName.trim(),
        motherName: form.motherName.trim() || null,
        fatherName: form.fatherName.trim() || null,
        motherMemberId: sameText(form.motherName, row.motherName) ? row.motherMemberId ?? null : null,
        fatherMemberId: sameText(form.fatherName, row.fatherName) ? row.fatherMemberId ?? null : null,
        photoKey: form.photoKey || row.photoKey || null,
      };
      if (kind === 'baptisms') {
        return api.patch<RegisterRow>(`/sacraments/baptisms/${rowId}`, {
          ...shared,
          ...(form.baptismDate ? { baptismDate: form.baptismDate } : {}),
          baptismNumber: form.baptismNumber.trim() || null,
          place: form.baptismPlace.trim() || null,
          ministerName: form.baptismMinister.trim() || null,
          baptismType: form.baptismType ?? row.baptismType ?? 'unknown',
        });
      }
      return api.patch<RegisterRow>(`/sacraments/confirmations/${rowId}`, {
        ...shared,
        ...(form.confirmationDate ? { confirmationDate: form.confirmationDate } : {}),
        confirmationNumber: form.confirmationNumber.trim() || null,
        place: form.confirmationPlace.trim() || null,
      });
    },
    onSuccess: async (updated) => {
      setCreateOpen(false);
      queryClient.setQueryData(['sacraments', kind, rowId], updated);
      toast.success(t('people.memberCreated'));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sacraments', kind] }),
        queryClient.invalidateQueries({ queryKey: ['people'] }),
      ]);
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  return (
    <>
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && (photoIndex !== null || createOpen)) {
          if (photoIndex !== null) setPhotoIndex(null);
          return;
        }
        onOpenChange(next);
      }}
    >
      <DialogContent
        size="lg"
        closeLabel={tCommon('action.close')}
        className="overflow-visible"
        onEscapeKeyDown={(event) => {
          if (createOpen) {
            event.preventDefault();
            return;
          }
          if (photoIndex === null) return;
          event.preventDefault();
          setPhotoIndex(null);
        }}
        onPointerDownOutside={(event) => {
          if (photoIndex !== null || createOpen) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (photoIndex !== null || createOpen) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {row ? personFor(kind, row) : t(`${kind}.detailTitle`)}
          </DialogTitle>
        </DialogHeader>
        <DialogBody contentClassName="flex flex-col gap-6">
          {detail.isLoading ? (
            <p className="text-body-sm text-fg-secondary">{tCommon('state.loading')}</p>
          ) : row ? (
            <dl className="grid gap-4 sm:grid-cols-2">
              {showFamily && (row.photoUrl || row.certificatePhotoUrl) ? (
                <div className="flex flex-wrap gap-6 sm:col-span-2">
                  {row.photoUrl ? (
                    <div>
                      <dt className="text-caption text-fg-secondary">{t('people.photo')}</dt>
                      <dd className="mt-1">
                        <button
                          type="button"
                          className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          onClick={() => setPhotoIndex(0)}
                          aria-label={t('people.expandPhoto', { name: personName })}
                        >
                          <MemberPhoto src={row.photoUrl} name={personName} size="xl" />
                        </button>
                      </dd>
                    </div>
                  ) : null}
                  {row.certificatePhotoUrl ? (
                    <div className="min-w-0 flex-1">
                      <dt className="text-caption text-fg-secondary">{t('people.certificatePhoto')}</dt>
                      <dd className="mt-1">
                        <button
                          type="button"
                          className="block max-w-sm overflow-hidden rounded-sm border border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          onClick={() => setPhotoIndex(row.photoUrl ? 1 : 0)}
                          aria-label={t('people.expandCertificate', { name: personName })}
                        >
                          <img
                            src={row.certificatePhotoUrl}
                            alt={t('people.expandCertificate', { name: personName })}
                            className="aspect-[3/2] w-full object-cover"
                          />
                        </button>
                      </dd>
                    </div>
                  ) : null}
                </div>
              ) : null}
              <DetailField label={t(`${kind}.columns.date`)}>
                {dateFor(kind, row) ? formatDate(dateFor(kind, row)!) : '—'}
              </DetailField>
              {kind === 'marriages' ? (
                <>
                  <DetailField label={t('marriages.fields.spouseOneMember')}>
                    <MemberValue
                      memberId={row.spouseOneMemberId}
                      name={row.spouseOneName}
                      viewLabel={t('fields.viewMember')}
                    />
                  </DetailField>
                  {([1, 2] as const).map((number) => (
                    <WitnessDetail
                      key={`spouse-one-witness-${number}`}
                      label={t('marriages.fields.spouseOneWitness', { number })}
                      witness={row.spouseOneWitnesses?.[number - 1]}
                      viewLabel={t('fields.viewMember')}
                    />
                  ))}
                  <DetailField label={t('marriages.fields.spouseTwoMember')}>
                    <MemberValue
                      memberId={row.spouseTwoMemberId}
                      name={row.spouseTwoName}
                      viewLabel={t('fields.viewMember')}
                    />
                  </DetailField>
                  {([1, 2] as const).map((number) => (
                    <WitnessDetail
                      key={`spouse-two-witness-${number}`}
                      label={t('marriages.fields.spouseTwoWitness', { number })}
                      witness={row.spouseTwoWitnesses?.[number - 1]}
                      viewLabel={t('fields.viewMember')}
                    />
                  ))}
                </>
              ) : (
                <DetailField label={t(`${kind}.columns.person`)}>
                  <MemberValue
                    memberId={row.memberId}
                    name={row.personName}
                    viewLabel={t('fields.viewMember')}
                  />
                </DetailField>
              )}
              {kind === 'transfers' ? (
                <>
                  <DetailField label={t('transfers.columns.direction')}>
                    {row.direction ? (
                      <Badge variant="neutral">{t(`transfers.direction.${row.direction}`)}</Badge>
                    ) : (
                      '—'
                    )}
                  </DetailField>
                  <DetailField label={t('transfers.columns.otherChurch')}>
                    {display(row.otherChurchName)}
                  </DetailField>
                  <DetailField label={t('transfers.columns.reference')}>
                    {display(row.letterReference)}
                  </DetailField>
                </>
              ) : null}
              {kind === 'funerals' ? (
                <>
                  <DetailField label={t('funerals.columns.funeralDate')}>
                    {row.serviceDate ? formatDate(row.serviceDate) : '—'}
                  </DetailField>
                  <DetailField label={t('funerals.columns.place')}>{display(row.place)}</DetailField>
                  <DetailField label={t('funerals.columns.officiant')}>
                    {display(row.officiantName)}
                  </DetailField>
                </>
              ) : null}
              {kind !== 'transfers' && kind !== 'funerals' ? (
                <>
                  <DetailField label={t(`${kind}.columns.place`)}>{display(row.place)}</DetailField>
                  <DetailField
                    label={t(
                      kind === 'marriages' ? 'marriages.columns.ministers' : `${kind}.columns.minister`,
                    )}
                  >
                    {kind === 'marriages' ? (
                      <MinisterList names={marriageMinisterNames(row)} />
                    ) : (
                      display(row.ministerName)
                    )}
                  </DetailField>
                  <DetailField label={t(`${kind}.columns.number`)}>
                    {display(certificateFor(kind, row))}
                  </DetailField>
                </>
              ) : null}
              {kind === 'baptisms' ? (
                <DetailField label={t('baptisms.fields.baptismType')}>
                  {row.baptismType ? t(`baptisms.type.${row.baptismType}`) : '—'}
                </DetailField>
              ) : null}
              {showFamily ? (
                <>
                  <DetailField label={t('people.mother')}>
                    <MemberValue
                      memberId={row.motherMemberId}
                      name={row.motherName}
                      viewLabel={t('fields.viewMember')}
                    />
                  </DetailField>
                  <DetailField label={t('people.father')}>
                    <MemberValue
                      memberId={row.fatherMemberId}
                      name={row.fatherName}
                      viewLabel={t('fields.viewMember')}
                    />
                  </DetailField>
                </>
              ) : null}
              {kind === 'marriages' ? (
                <DetailField label={t('marriages.fields.marriageType')}>
                  {row.marriageType ? t(`marriages.type.${row.marriageType}`) : '—'}
                </DetailField>
              ) : null}
              {row.notes ? (
                <div className="sm:col-span-2">
                  <dt className="text-caption text-fg-secondary">{t(`${kind}.fields.notes`)}</dt>
                  <dd className="whitespace-pre-wrap text-body-sm text-fg">{row.notes}</dd>
                </div>
              ) : null}
            </dl>
          ) : (
            <p className="text-body-sm text-fg-secondary">{t(`${kind}.empty`)}</p>
          )}
        </DialogBody>
        <DialogFooter className="justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {tCommon('action.close')}
          </Button>
          {canCreateMember && showFamily && row && !row.memberId ? (
            <Button variant="outline" onClick={() => setCreateOpen(true)}>
              {t('people.createProfile')}
            </Button>
          ) : null}
          {canEdit && row ? (
            <Button onClick={() => onEdit(row)}>{tCommon('action.edit')}</Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {row && (row.photoUrl || row.certificatePhotoUrl) ? (
      <ImageLightbox
        images={[row.photoUrl, row.certificatePhotoUrl].filter((url): url is string => Boolean(url))}
        index={photoIndex}
        onClose={() => setPhotoIndex(null)}
        onIndexChange={setPhotoIndex}
        caption={personName}
        labels={{
          dialogLabel: tCommon('lightbox.title'),
          close: tCommon('lightbox.close'),
          previous: tCommon('lightbox.previous'),
          next: tCommon('lightbox.next'),
          count: tCommon('lightbox.imageCount', {
            current: (photoIndex ?? 0) + 1,
            total: [row.photoUrl, row.certificatePhotoUrl].filter(Boolean).length,
          }),
          imageAlt: t('people.expandPhoto', { name: personName }),
        }}
      />
    ) : null}
    {row && showFamily ? (
      <MemberCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        resetKey={row.id}
        initialValues={memberFormFromRegister(kind, row)}
        title={t('people.createProfile')}
        description={t('people.createProfileDescription')}
        submitting={createProfile.isPending}
        onSubmit={async (form) => {
          try {
            await createProfile.mutateAsync(form);
          } catch {
            // The mutation reports the error.
          }
        }}
      />
    ) : null}
    </>
  );
};

const WitnessDetail = ({
  label,
  witness,
  viewLabel,
}: {
  label: string;
  witness?: MarriageWitness;
  viewLabel: string;
}) => (
  <DetailField label={label}>
    <MemberValue memberId={witness?.memberId} name={witness?.name} viewLabel={viewLabel} />
  </DetailField>
);

const MinisterList = ({ names }: { names: string[] }) => {
  if (names.length === 0) return <>{'—'}</>;
  return (
    <span className="flex flex-col gap-1">
      {names.map((name, index) => (
        <span key={`${index}-${name}`}>{name}</span>
      ))}
    </span>
  );
};

const MemberValue = ({
  memberId,
  name,
  viewLabel,
}: {
  memberId?: string | null;
  name?: string | null;
  viewLabel: string;
}) => {
  const label = display(name);
  if (!memberId) return <>{label}</>;
  return (
    <span className="flex flex-col gap-1">
      <span>{label}</span>
      <Link
        to={`/people/${memberId}`}
        className="text-caption text-fg-brand hover:underline"
      >
        {viewLabel}
      </Link>
    </span>
  );
};
